import React, { forwardRef, InputHTMLAttributes, Ref } from 'react'

import CustomSelect from '@/components/base/custom-select'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPCF7FormField } from '@/types'

type FormControlVariant = 'default' | 'newsletter' | 'outlined' | 'light'

type FormControlProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur'
> &
  Partial<
    WPCF7FormField & {
      error?: boolean
      errorMessage?: string
      visibility?: 'visible' | 'hidden' | 'collapsed'
      options?: { value: string; text: string }[]
      resetKey?: string
      variant?: FormControlVariant
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
    }
  >

const FormControl = forwardRef(
  (
    {
      id,
      type,
      label,
      checked,
      choices,
      placeholder,
      className,
      resetKey,
      children,
      error,
      errorMessage,
      visibility,
      variant = 'default',
      adminLabel: _adminLabel,
      ...props
    }: FormControlProps,
    ref: Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const Component = (type === 'textarea' ? 'textarea' : 'input') as any

    return (
      <div
        className={cn(className, {
          hidden: type === 'hidden' || visibility === 'hidden'
        })}
      >
        {type === 'consent' ? (
          <label
            className={cn([
              'relative flex select-none items-center',
              {
                'pointer-events-none opacity-40': props.disabled
              }
            ])}
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              className={cn([
                'size-[1.3rem] opacity-0',
                '[&:checked+*]:after:opacity-100 [&:focus+*]:before:opacity-100'
              ])}
              {...props}
            />
            <div
              className={cn([
                'absolute left-0 top-[-2px] size-[1.3rem] rounded-full border before:absolute before:-left-[0.35rem] before:-top-[0.35rem] before:size-[1.8rem] before:rounded-full before:border-4 before:opacity-100 after:absolute after:left-[0.2rem] after:top-[0.2rem] after:size-[0.7rem] after:rounded-full after:opacity-0',
                variant !== 'light' && [
                  'border-[black] dark:border-[white]',
                  'before:border-[black]/10 dark:before:border-[white]/25',
                  'after:bg-[black] dark:after:bg-[white]'
                ],
                variant === 'light' && [
                  'border-[#8c8c8c]',
                  'before:border-border-secondary-light/10',
                  'after:bg-border-secondary-light/80'
                ],
                {
                  'border-[red] before:border-[red]/30': error,
                  'dark:border-[red] dark:before:border-[red]/30': error
                }
              ])}
            />
            <Typography
              variant="regular"
              className={cn([
                'ml-[1rem] font-helvetica text-[1.2rem] leading-[120%] tracking-[0.036rem]',
                variant !== 'light' && 'dark:text-[white]',
                variant === 'light' && 'text-text-primary-light',
                '[&_a]:underline',
                {
                  '!text-[red]': error
                }
              ])}
            >
              {label}
            </Typography>
          </label>
        ) : type === 'select' ? (
          <div className="flex flex-col">
            {(variant === 'default' || variant === 'light') && label ? (
              <Typography
                variant="h6"
                component="span"
                className={cn([
                  'text-text-tertiary-light dark:text-text-tertiary-dark mb-[1rem]',
                  {
                    '!text-[red]': error,
                    'text-text-heading-light': variant === 'light',
                    'pointer-events-none opacity-40': props.disabled
                  }
                ])}
              >
                {label}
                {props.required ? ' *' : ''}
              </Typography>
            ) : null}
            <CustomSelect
              ref={ref as Ref<HTMLButtonElement>}
              options={
                choices?.map((choice) => ({
                  value: choice.value,
                  text: choice.text
                })) || []
              }
              placeholder={placeholder}
              onChange={props.onChange}
              onReset={() => props.onChange?.(null)}
              error={error}
              disabled={props.disabled}
              resetKey={resetKey}
              variant={variant}
            />
          </div>
        ) : type === 'checkbox' ||
          type === 'radio' ||
          type === 'multi_choice' ? (
          <fieldset
            className={cn([
              'flex flex-col gap-y-spacing-xs',
              { 'pointer-events-none opacity-40': props.disabled }
            ])}
          >
            {label ? (
              <Typography
                variant="h6"
                component="span"
                className={cn([
                  'text-text-tertiary-light dark:text-text-tertiary-dark mb-[5rem]',
                  { '!text-[red]': error }
                ])}
              >
                {label}
                {props.required ? ' *' : ''}
              </Typography>
            ) : null}
            {choices?.map((choice, i) => {
              const choiceId = `${id}_${i}`
              const isMultiple = type === 'checkbox' || type === 'multi_choice'
              const isChecked = isMultiple
                ? Array.isArray(props.value) &&
                  props.value.includes(choice.value)
                : props.value === choice.value

              return (
                <label
                  key={choiceId}
                  className="relative flex cursor-pointer items-center"
                >
                  <input
                    id={choiceId}
                    name={type === 'radio' ? id : choiceId}
                    type={type === 'radio' ? 'radio' : 'checkbox'}
                    value={choice.value}
                    checked={isChecked}
                    className="absolute size-[1.4rem] cursor-pointer opacity-0"
                    onChange={() => {
                      if (isMultiple) {
                        const current = Array.isArray(props.value)
                          ? props.value
                          : []
                        const next = current.includes(choice.value)
                          ? current.filter((v: string) => v !== choice.value)
                          : [...current, choice.value]
                        props.onChange?.(next)
                      } else {
                        props.onChange?.(choice.value)
                      }
                    }}
                    disabled={props.disabled}
                  />
                  <div
                    className={cn([
                      'flex size-[1.4rem] shrink-0 items-center justify-center rounded-[0.2rem] border border-[black]/30 transition-colors dark:border-[white]/30',
                      {
                        'border-[black] bg-[black] dark:border-[white] dark:bg-[white]':
                          isChecked,
                        'border-[red] dark:border-[red]': error
                      }
                    ])}
                  >
                    {isChecked ? (
                      <svg
                        width="10"
                        height="8"
                        viewBox="0 0 10 8"
                        fill="none"
                        className="text-[white] dark:text-[black]"
                      >
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </div>
                  <Typography
                    variant="regular"
                    className={cn('condensed ml-[1.6rem]', {
                      'text-text-primary-light': variant === 'light'
                    })}
                  >
                    {choice.text}
                  </Typography>
                </label>
              )
            })}
          </fieldset>
        ) : (
          <div className="flex flex-col">
            {(variant === 'default' || variant === 'light') && label ? (
              <Typography
                variant="h6"
                component="span"
                className={cn([
                  'mb-[1rem]',
                  variant === 'default' &&
                    'text-text-tertiary-light dark:text-text-tertiary-dark',
                  variant === 'light' && 'text-text-heading-light',
                  { '!text-[red]': error },
                  {
                    'pointer-events-none opacity-40': props.disabled
                  }
                ])}
              >
                {label}
                {props.required ? ' *' : ''}
              </Typography>
            ) : null}
            <Component
              id={id}
              type={type}
              placeholder={placeholder}
              className={cn([
                'flex w-full appearance-none bg-transparent outline-none',
                'font-helvetica',
                variant === 'newsletter' && [
                  'rounded-radius-minimal border border-[black]/10 px-spacing-sm pb-[1.9rem] pt-[2.2rem] placeholder:text-[black] dark:border-[white]/20 dark:placeholder:text-[white]',
                  'focus:border-[black]/30 focus:ring-2 focus:ring-[black]/10 dark:focus:border-[white]/40 dark:focus:ring-[white]/20'
                ],
                variant === 'default' && [
                  'h-[5rem] border-b border-[black]/20 pb-spacing-2xs pt-spacing-3xs placeholder:text-[black]/40 dark:border-[white]/20 dark:placeholder:text-[white]/40',
                  'focus:border-[black]/50 dark:focus:border-[white]/50'
                ],
                variant === 'light' && [
                  'h-[5rem] border-b border-border-secondary-light pb-spacing-2xs pt-spacing-3xs text-text-primary-light placeholder:text-text-primary-light/40',
                  'focus:border-border-secondary-light/70'
                ],
                { 'min-h-[15rem]': type === 'textarea' },
                {
                  'border-[red] focus:border-[red] focus:ring-[red]/50': error,
                  'dark:border-[red] dark:focus:border-[red] dark:focus:ring-[red]/50':
                    error,
                  'pointer-events-none opacity-40': props.disabled
                }
              ])}
              ref={type !== 'consent' && type !== 'select' ? ref : undefined}
              {...props}
            />
          </div>
        )}
        {children}
        {error && errorMessage ? (
          <Typography
            variant="regular"
            component="span"
            className="mt-[0.6rem] block font-helvetica text-[1.1rem] leading-[120%] text-[red]"
          >
            {errorMessage}
          </Typography>
        ) : null}
      </div>
    )
  }
)

FormControl.displayName = 'FormControl'

export default FormControl
